<template>
  <div class="modal-overlay" @click.self="close">
    <div class="modal-content">
      <div class="modal-header">
        <h2>{{ isEdit ? '编辑成绩' : '添加成绩' }}</h2>
        <button class="close-btn" @click="close">&times;</button>
      </div>

      <form @submit.prevent="submit">
        <div class="form-group">
          <label for="student_id">學生 *</label>
          <select 
            v-if="!isEdit && !student"
            id="student_id" 
            v-model="form.student_id" 
            required
            :disabled="isEdit"
          >
            <option value="">請選擇學生</option>
            <option 
              v-for="student in availableStudents" 
              :key="student.student_id" 
              :value="student.student_id"
            >
              {{ student.name }} (ID: {{ student.student_id }})
            </option>
          </select>
          <!-- 其他情況直接顯示學生姓名 -->
          <div v-else class="fixed-student-info">
            <strong>{{ displayStudent.name }}</strong> (ID: {{ displayStudent.student_id }})
          </div>
          <p class="helper-text" v-if="!isEdit && !student">請選擇要新增成績的學生</p>
          <p class="helper-text" v-else>此成績將關聯到指定學生</p>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label for="score">分數</label>
            <input 
              id="score" 
              type="number" 
              v-model="form.score" 
              :max="exam?.full_score || 100"
              min="0" 
              step="0.1"
              placeholder="輸入分數"
              @input="calculatePercentage"
            >
            <p class="helper-text" v-if="exam?.full_score">
              滿分: {{ exam.full_score }}
            </p>
          </div>

          <div class="form-group">
            <label for="percentage">百分比</label>
            <input 
              id="percentage" 
              type="number" 
              v-model="form.percentage" 
              min="0" 
              max="100"
              step="0.1"
              placeholder="自動計算"
              readonly
            >
            <p class="helper-text">根據分數自動計算</p>
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label for="grade">等第</label>
            <select id="grade" v-model="form.grade">
              <option value="">請選擇等第</option>
              <option v-for="gradeOption in gradeOptions" :key="gradeOption" :value="gradeOption">
                {{ gradeOption }}
              </option>
            </select>
          </div>

          <div class="form-group">
            <label for="rank">排名</label>
            <input 
              id="rank" 
              type="number" 
              v-model="form.rank" 
              min="1"
              placeholder="輸入排名"
            >
          </div>
        </div>

        <div class="form-group">
          <label for="notes">備註</label>
          <textarea 
            id="notes" 
            v-model="form.notes" 
            rows="3"
            placeholder="可選的備註..."
          ></textarea>
        </div>

        <div class="form-actions">
          <button type="button" class="cancel-btn" @click="close">取消</button>
          <button type="submit" class="submit-btn">提交</button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useMutation, useQuery } from '@vue/apollo-composable';
import { CREATE_STUDENT_EXAM_SCORE, UPDATE_STUDENT_EXAM_SCORE } from '../graphql/queries';
import { GET_STUDENTS, GET_STUDENTS_IN_COURSE } from '../graphql/queries';

const props = defineProps({
  score: Object,
  exam: Object,
  isEdit: Boolean,
  student: Object
});

const emit = defineEmits(['close', 'saved']);
const course_id = computed(() => props.exam?.session?.course?.course_id || undefined);

// 獲取所有學生資料
const { result: studentsResult } = useQuery(GET_STUDENTS,
  undefined,
  { enabled: !props.isEdit && !props.student} // 在新增模式下且未指定學生時才載入全部學生
);
const { result: studentsInCourseResult } = useQuery(GET_STUDENTS_IN_COURSE,
 { course_id }, 
 { enabled: !!course_id.value && !props.isEdit && !props.student }
);

const allStudents = computed(() => studentsResult.value?.students || []);
const enrolledStudents = computed(() => studentsInCourseResult.value?.studentsInCourse.map(s => s.student) || []);

const availableStudents = computed(() => {
  // 編輯模式之下，載入正在編輯的學生
  if (props.isEdit || props.student) {
    return [];
  }
  // 在新增模式下，過濾掉已經有成績的學生
  // 如果是補習班的考試，優先顯示該門課的學生
  if(enrolledStudents.value?.length > 0) {
    return enrolledStudents.value.concat(allStudents.value.filter(s => 
      !enrolledStudents.value.some(es => es.student_id === s.student_id)
    ));
  }
  // 否則顯示所有學生
  return allStudents.value;
});

// 計算要顯示的學生資訊
const displayStudent = computed(() => {
  // 編輯模式：顯示成績記錄中的學生
  if (props.isEdit && props.score?.student) {
    return props.score.student;
  }
  // 新增模式且有指定學生：顯示指定的學生
  if (!props.isEdit && props.student) {
    return props.student;
  }
  // 其他情況返回空物件（不會顯示，因為會顯示下拉選單）
  return {};
});

// 等第選項
const gradeOptions = ['A', 'B', 'C', 'D', 'F', '優', '甲', '乙', '丙', '丁'];

// 表單資料
const defaultForm = {
  student_id: '',
  score: null,
  percentage: null,
  grade: '',
  rank: null,
  notes: ''
};

const form = ref({ ...defaultForm });

// 監聽 score prop 變化
watch([() => props.score, () => props.student], ([score, student]) => {
  if (score) {
    form.value = {
      student_id: score.student_id.toString(),
      score: score.score,
      percentage: score.percentage,
      grade: score.grade || '',
      rank: score.rank,
      notes: score.notes || ''
    };
  } else if (student) {
    form.value = {
      ...defaultForm,
      student_id: student.student_id.toString()
    };
  } else {
    resetForm();
  }
}, { immediate: true });

const { mutate: createScore } = useMutation(CREATE_STUDENT_EXAM_SCORE);
const { mutate: updateScore } = useMutation(UPDATE_STUDENT_EXAM_SCORE);

function resetForm() {
  form.value = { ...defaultForm };
}

function calculatePercentage() {
  if (form.value.score && props.exam?.full_score) {
    const percentage = (form.value.score / props.exam.full_score) * 100;
    form.value.percentage = parseFloat(percentage.toFixed(1));
  } else {
    form.value.percentage = null;
  }
}

function close() {
  resetForm();
  emit('close');
}

function submit() {
  // 驗證必填欄位
  if (!form.value.student_id) {
    alert('請選擇學生');
    return;
  }
  const input = {
    student_id: form.value.student_id,
    exam_id: props.exam.exam_id,
    score: form.value.score ? parseFloat(form.value.score) : null,
    percentage: form.value.percentage ? parseFloat(form.value.percentage) : null,
    grade: form.value.grade || null,
    rank: form.value.rank ? parseInt(form.value.rank) : null,
    notes: form.value.notes || null
  };

  if (props.isEdit) {
    updateScore({
      id: props.score.id,
      input
    }).then(() => {
      emit('saved');
      resetForm();
    }).catch((error) => {
      console.error('更新成績失敗:', error);
      alert('更新成績失敗: ' + error.message);
    });
  } else {
    createScore({ input }).then(() => {
      emit('saved');
      resetForm();
    }).catch((error) => {
      console.error('創建成績失敗:', error);
      alert('創建成績失敗: ' + error.message);
    });
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background-color: white;
  border-radius: 8px;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #eee;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0;
}

form {
  padding: 20px;
}

.form-group {
  margin-bottom: 20px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
}

label {
  display: block;
  margin-bottom: 8px;
  font-weight: bold;
}

input, select, textarea {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-sizing: border-box;
  font-size: 0.9rem;
}

input:focus, select:focus, textarea:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.2);
}

input:readonly {
  background-color: #f5f5f5;
  color: #666;
}

.helper-text {
  margin: 5px 0 0 0;
  font-size: 0.8rem;
  color: var(--text-light);
  font-style: italic;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 30px;
}

.cancel-btn {
  padding: 10px 20px;
  background-color: #f5f5f5;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
}

.submit-btn {
  padding: 10px 20px;
  background-color: var(--primary-color);
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
}

.submit-btn:hover {
  background-color: var(--primary-hover);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .form-row {
    grid-template-columns: 1fr;
    gap: 0;
  }
  
  .modal-content {
    margin: 20px;
    max-height: calc(100vh - 40px);
  }
}
</style>